import React, {
  ComponentProps,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useState,
} from "react";
import {
  Circle,
  Label,
  ScrollView,
  styled,
  Text,
  RadioGroup as TGRadioGroup,
  XStack,
  YStack,
} from "tamagui";
import { LinearGradient } from "tamagui/linear-gradient";

import { useScrollbarFlash } from "../../hooks";
import { stylex } from "../../utils";
import { Key } from "./key";

const RadioGroupContainerBase = styled(TGRadioGroup, {
  name: "RadioGroupContainer",
  variants: {
    variant: {
      default: {
        flexWrap: "wrap",
      },
    },
    size: {
      gap: "$2",
    },
  } as const,
  defaultVariants: {
    variant: "default",
  },
});

const RadioGroupLabel = styled(Label, {
  name: "RadioGroupLabel",
  cursor: "pointer",
  flex: 1,
  alignSelf: "stretch",
  variants: {
    variant: {
      default: {
        borderColor: "$gray-800",
        backgroundColor: "white",
        borderWidth: "$0.2",
        hoverStyle: {
          borderColor: "$gray-secondary-400",
          backgroundColor: "$gray-50",
        },
        focusStyle: {
          borderColor: "$gray-primary-900",
          backgroundColor: "$gray-50",
        },
        disabledStyle: {
          borderColor: "$gray-50",
          backgroundColor: "$gray-50",
          cursor: "not-allowed",
        },
      },

      plain: {
        backgroundColor: "white",
        borderWidth: 0,
        disabledStyle: {
          cursor: "not-allowed",
        },
      },
    },
    size: {
      lg: {
        paddingHorizontal: "$3",
        minHeight: "$8.5",
      },
      md: {
        minHeight: "$7.4",
        paddingHorizontal: "$3",
      },
      sm: {
        minHeight: "$3",
        paddingHorizontal: 0,
      },
    },
    rounded: {
      lg: {
        borderRadius: "$4.5",
      },
      md: {
        borderRadius: "$2",
      },
    },
    selected: {
      true: {
        borderColor: "$gray-800",
        backgroundColor: "$gray-200",
        hoverStyle: {
          borderColor: "$gray-800",
          backgroundColor: "$gray-200",
        },
      },
      false: {},
    },
  } as const,
  defaultVariants: {
    size: "lg",
    rounded: "md",
    variant: "default",
    selected: false,
  },
});

const RadioGroupItem = styled(TGRadioGroup.Item, {
  name: "RadioGroupItem",
  variants: {
    variant: {
      default: { opacity: 0 },
    },
  } as const,
  defaultVariants: {
    variant: "default",
  },
});

const ItemText = styled(Text, {
  name: "ItemText",
  width: "100%",
  flex: 1,
  variants: {
    variant: {
      default: {
        color: "$gray-primary-900",
        disabledStyle: {
          color: "$gray-secondary-400",
        },
      },
    },
    size: {
      lg: {
        fontFamily: "$subtitle",
        fontSize: "$5",
        fontWeight: "$6",
        lineHeight: "$4",
      },
      md: {
        fontFamily: "$body",
        fontSize: "$3",
        fontWeight: "$6",
        lineHeight: "$1",
      },
      sm: {
        fontFamily: "$body",
        fontSize: "$3",
        fontWeight: "$4",
        lineHeight: "$1",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    size: "lg",
  },
});

const ItemSubText = styled(Text, {
  name: "ItemSubText",
  width: "100%",
  flex: 1,
  variants: {
    variant: {
      default: {
        color: "$gray-primary-900",
        disabledStyle: {
          color: "$gray-secondary-400",
        },
      },
    },
    size: {
      lg: {
        fontFamily: "$subtitle",
        fontSize: "$4",
        fontWeight: "400",
        lineHeight: "$4",
      },
      md: {
        fontFamily: "$body",
        fontSize: "$2",
        fontWeight: "400",
        lineHeight: "$1",
      },
      sm: {
        fontFamily: "$body",
        fontSize: "$2",
        fontWeight: "400",
        lineHeight: "$1",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    size: "lg",
  },
});

const RadioGroupIndicatorContainer = styled(Circle, {
  name: "RadioGroupIndicatorContainer",
  justifyContent: "center",
  alignItems: "center",
  variants: {
    variant: {
      default: {
        borderWidth: "$0.25",
        borderColor: "$gray-800",
        backgroundColor: "transparent",
        hoverStyle: {
          backgroundColor: "$gray-200",
        },
        focusStyle: {
          backgroundColor: "white",
        },
        disabledStyle: {
          backgroundColor: "white",
          borderColor: "$gray-200",
        },
      },
    },
    size: {
      lg: {
        height: "$2",
        width: "$2",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    size: "lg",
  },
});

type RadioGroupContainerProps = ComponentProps<
  typeof RadioGroupContainerBase
> & { withScroll?: boolean; sm?: boolean };

const RadioGroupContext = createContext<{ selected: string | undefined }>(
  null!,
);

const RadioGroupContainer: React.FC<RadioGroupContainerProps> = ({
  onValueChange,
  withScroll,
  value,
  maxHeight,
  sm,
  ...rest
}) => {
  const [selected, setSelected] = useState<string | undefined>(value);

  const handleChange = useCallback(
    (value: string) => {
      onValueChange?.(value);
      setSelected(value);
    },
    [onValueChange],
  );

  const { scrollRef } = useScrollbarFlash();

  const [showBottomShadow, setShowBottomShadow] = useState(true);
  const [showTopShadow, setShowTopShadow] = useState(false);

  return (
    <RadioGroupContext.Provider value={{ selected }}>
      {withScroll ? (
        <YStack position="relative" flex={1}>
          <ScrollView
            ref={scrollRef}
            onScroll={(event) => {
              const { contentOffset, contentSize, layoutMeasurement } =
                event.nativeEvent;
              setShowTopShadow(contentOffset.y >= 10);
              setShowBottomShadow(
                !(
                  contentOffset.y >=
                  contentSize.height - layoutMeasurement.height - 10
                ),
              );
            }}
            scrollEventThrottle={16}
            maxHeight={maxHeight}
          >
            <RadioGroupContainerBase
              value={selected}
              onValueChange={handleChange}
              {...rest}
            />
          </ScrollView>
          {showTopShadow && (
            <LinearGradient
              colors={[sm ? "white" : "$bgpeach-500", "transparent"]}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 30,
                pointerEvents: "none",
                zIndex: 50,
              }}
            />
          )}
          {showBottomShadow && (
            <LinearGradient
              colors={["transparent", sm ? "white" : "$bgpeach-500"]}
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: 30,
                pointerEvents: "none",
                zIndex: 50,
              }}
            />
          )}
        </YStack>
      ) : (
        <RadioGroupContainerBase
          value={selected}
          onValueChange={handleChange}
          maxHeight={maxHeight}
          {...rest}
        />
      )}
    </RadioGroupContext.Provider>
  );
};

type RadioGroupIndicatorProps = ComponentProps<
  typeof RadioGroupIndicatorContainer
>;

const RadioGroupIndicator: React.FC<
  RadioGroupIndicatorProps & { value?: string }
> = ({ value, ...rest }) => {
  const { selected } = useContext(RadioGroupContext);
  return (
    <RadioGroupIndicatorContainer {...rest}>
      {selected === value && (
        <Circle
          backgroundColor={"$lightgreen-500"}
          height={"$1"}
          width={"$1"}
        />
      )}
    </RadioGroupIndicatorContainer>
  );
};

type RadioGroupItemProps = ComponentProps<typeof RadioGroupLabel>;

const RadioGroupKey: React.FC<{
  count: number | string;
  text: string | number;
  isSelected: boolean;
  disabled: boolean;
  size?: "lg" | "md" | "sm";
}> = ({ size, isSelected, count, text, disabled }) => {
  return (
    <XStack gap={"$1"}>
      {text.toString().length > 0 && (
        <Text
          $group-hover={{
            ...stylex(
              [
                isSelected,
                {
                  color: "$gray-600",
                },
              ],
              [
                !isSelected,
                {
                  color: "$gray-secondary-400",
                },
              ],
            ),
          }}
          $group-focus={{
            color: "$gray-secondary-400",
          }}
          color={"$gray-600"}
          fontFamily={"$body"}
          fontSize={"$3"}
          fontWeight={"$4"}
          lineHeight={"$3"}
          {...stylex([
            disabled,
            {
              color: "$gray-secondary-400",
            },
          ])}
        >
          {text}
        </Text>
      )}
      <Key size={size} disabled={disabled} text={count} />
    </XStack>
  );
};

type RenderKey = (props: {
  isSelected: boolean;
  disabled: boolean;
}) => React.ReactNode;

export type RadioGroupRef = HTMLButtonElement | null;

const RadioGroupWithLabel = forwardRef<
  RadioGroupRef,
  RadioGroupItemProps & {
    value: string;
    index: number;
    renderKey?: RenderKey;
    text: string;
  }
>(
  (
    { renderKey, children, value, disabled, index, text, size = "lg", ...rest },
    ref,
  ) => {
    const { selected } = useContext(RadioGroupContext);
    const radioItemId = "radio-group-item-id-" + value;
    const isSelected = selected === value;

    const heavy = text.includes("\n");

    return (
      <RadioGroupLabel
        disabled={disabled}
        group={true}
        htmlFor={radioItemId}
        selected={isSelected}
        size={size}
        {...rest}
      >
        <RadioGroupItem
          ref={ref}
          disabled={disabled}
          id={radioItemId}
          value={value}
        />
        <XStack justifyContent="space-between" alignItems="center" flex={1}>
          <XStack
            {...stylex(
              [size === "lg", { gap: "$1.5", height: heavy ? "$12" : "$8" }],
              [size === "md", { gap: "$2", maxHeight: heavy ? "$11" : "$7.4" }],
              [size === "sm", { gap: "$1", maxHeight: heavy ? "$3" : "$3" }],
            )}
            flex={1}
            paddingRight={"$2"}
            alignSelf="stretch"
            alignItems="center"
          >
            <RadioGroupIndicator disabled={disabled} value={value} />
            <YStack maxWidth={"100%"}>
              {text.split("\n").map((line, i) => {
                if (i > 0) {
                  return (
                    <ItemSubText key={i} size={size} disabled={disabled}>
                      {line}
                    </ItemSubText>
                  );
                }
                return (
                  <ItemText key={i} size={size} disabled={disabled}>
                    {line}
                  </ItemText>
                );
              })}
            </YStack>
          </XStack>
          {size === "lg"
            ? renderKey?.({ isSelected, disabled: !!disabled })
            : null}
        </XStack>
      </RadioGroupLabel>
    );
  },
);

RadioGroupWithLabel.displayName = "RadioGroupWithLabel";

export const RadioGroup = {
  Container: RadioGroupContainer,
  Item: RadioGroupWithLabel,
  Key: RadioGroupKey,
};
